/**
 * HARUM (HAR-126): "Different Warehouse Notice" popup.
 *
 * Ported from the Expanse live theme (assets/warehouse-checker.js +
 * its inline integration). Products tagged "China Warehouse" ship from
 * China; everything else ships from Korea. When the cart already holds
 * items from one warehouse and the shopper adds an item from the other,
 * a modal recommends splitting the order. "Add Anyway" proceeds.
 *
 * HYPER adaptation: the Expanse theme intercepted a bespoke add-to-cart
 * button click. Hyper's product form is `<form is="product-form">` which
 * handles the `submit` event and does an AJAX add. We intercept that
 * submit in the CAPTURE phase on `document` (so we run before the
 * product-form's own submit handler), and re-submit with a bypass flag
 * once the check passes / the shopper confirms.
 */

class HarumWarehouseChecker {
  constructor() {
    this.WAREHOUSE_TAG = 'China Warehouse';
    this._productCache = {};
  }

  hasWarehouseTag(tags) {
    if (!tags) return false;
    if (!Array.isArray(tags)) {
      if (typeof tags === 'string') {
        tags = tags.split(',').map((t) => t.trim());
      } else {
        return false;
      }
    }
    return tags.some((tag) => tag === this.WAREHOUSE_TAG);
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('[HarumWarehouseChecker] Error fetching cart:', error);
      return null;
    }
  }

  async getProductByHandle(handle) {
    if (this._productCache[handle]) return this._productCache[handle];
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');
      const product = await response.json();
      this._productCache[handle] = product;
      return product;
    } catch (error) {
      console.error('[HarumWarehouseChecker] Error fetching product:', error);
      return null;
    }
  }

  /**
   * Decide whether the product (identified by its tags) can be added to
   * the current cart without a warehouse conflict.
   * Returns { canAdd, message }.
   */
  async checkWarehouseCompatibility(productTags) {
    const cart = await this.getCart();
    if (!cart || !cart.items || cart.items.length === 0) {
      return { canAdd: true, message: '' };
    }

    const currentProductHasWarehouse = this.hasWarehouseTag(productTags);

    let hasKoreaWarehouse = false;
    let hasChinaWarehouse = false;

    // Fetch each distinct cart item's tags to classify its warehouse.
    const handles = [...new Set(cart.items.map((item) => item.handle))];
    const products = await Promise.all(handles.map((h) => this.getProductByHandle(h)));
    for (const product of products) {
      const tags = (product && product.tags) || [];
      if (this.hasWarehouseTag(tags)) {
        hasChinaWarehouse = true;
      } else {
        hasKoreaWarehouse = true;
      }
    }

    // Cart already mixes warehouses -> nothing new to warn about.
    if (hasKoreaWarehouse && hasChinaWarehouse) {
      return { canAdd: true, message: '' };
    }

    const conflictMessage =
      'Your cart contains products from a different warehouse. We recommend placing products from different countries in separate orders to save on shipping costs.';

    if (currentProductHasWarehouse && hasKoreaWarehouse) {
      return { canAdd: false, message: conflictMessage };
    }
    if (!currentProductHasWarehouse && hasChinaWarehouse) {
      return { canAdd: false, message: conflictMessage };
    }
    return { canAdd: true, message: '' };
  }

  /** Builds the "Different Warehouse Notice" modal (matches Expanse). */
  showWarningDialog(message, onConfirm, onCancel) {
    const existing = document.querySelector('.harum-warehouse-modal-backdrop');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    const backdrop = document.createElement('div');
    backdrop.className = 'harum-warehouse-modal-backdrop';

    const modal = document.createElement('div');
    modal.className = 'harum-warehouse-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Different Warehouse Notice');
    modal.innerHTML = `
      <div class="harum-warehouse-modal__content">
        <div class="harum-warehouse-modal__icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 class="harum-warehouse-modal__title">Different Warehouse Notice</h2>
        <p class="harum-warehouse-modal__message"></p>
        <div class="harum-warehouse-modal__buttons">
          <button type="button" class="harum-warehouse-modal__cancel">Cancel</button>
          <button type="button" class="harum-warehouse-modal__confirm">Add Anyway</button>
        </div>
      </div>
    `;
    // Inject the message as text (never as HTML).
    modal.querySelector('.harum-warehouse-modal__message').textContent = message;

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    const cancelBtn = modal.querySelector('.harum-warehouse-modal__cancel');
    const confirmBtn = modal.querySelector('.harum-warehouse-modal__confirm');

    let closed = false;
    const closeModal = (callback) => {
      if (closed) return;
      closed = true;
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
      if (callback) callback();
    };

    cancelBtn.addEventListener('click', () => closeModal(onCancel));
    confirmBtn.addEventListener('click', () => closeModal(onConfirm));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) closeModal(onCancel);
    });
    document.addEventListener(
      'keydown',
      function escHandler(e) {
        if (e.key === 'Escape') {
          document.removeEventListener('keydown', escHandler);
          closeModal(onCancel);
        }
      }
    );
    confirmBtn.focus();
  }
}

window.HarumWarehouseChecker = HarumWarehouseChecker;

/**
 * <harum-warehouse-guard data-form-id="product-form-xxx">
 *   <script type="application/json">["tag1","tag2",...]</script>
 * </harum-warehouse-guard>
 *
 * Guards the matching product form's add-to-cart submit.
 */
if (!customElements.get('harum-warehouse-guard')) {
  customElements.define(
    'harum-warehouse-guard',
    class HarumWarehouseGuard extends HTMLElement {
      connectedCallback() {
        this.checker = new HarumWarehouseChecker();
        this.bypass = false;
        this.formId = this.dataset.formId;

        const configEl = this.querySelector('script[type="application/json"]');
        try {
          this.productTags = configEl ? JSON.parse(configEl.textContent) : [];
        } catch (e) {
          this.productTags = [];
        }

        // Capture-phase on document so we run before <form is="product-form">'s
        // own submit handler.
        this.onSubmit = this.handleSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
      }

      handleSubmit(event) {
        const form = event.target;
        // NB: use getAttribute('id') — the form has an <input name="id"> (the
        // variant id) which shadows form.id (it returns that input element,
        // not the id string), so form.id would never match.
        if (!form || form.getAttribute('id') !== this.formId) return;

        // Confirmed / already-checked pass-through.
        if (this.bypass) {
          this.bypass = false;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        this.checker
          .checkWarehouseCompatibility(this.productTags || [])
          .then((result) => {
            if (result.canAdd) {
              this.resubmit(form);
            } else {
              this.checker.showWarningDialog(
                result.message,
                () => this.resubmit(form),
                () => {}
              );
            }
          })
          .catch((error) => {
            console.error('[HarumWarehouseGuard] check failed, allowing add:', error);
            this.resubmit(form);
          });
      }

      resubmit(form) {
        this.bypass = true;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          // Fallback for older browsers.
          const btn = form.querySelector('[type="submit"][name="add"]');
          if (btn) btn.click();
          else form.submit();
        }
      }
    }
  );
}
